export type ExamBoard = 'AQA' | 'OCR' | 'Edexcel' | 'WJEC';
export type Level = 'GCSE' | 'AS-Level' | 'A-Level';
export type Subject = 'Computer Science' | 'ICT' | 'Python';
export type Qualification = 'all' | 'gcse-cs' | 'gcse-ict' | 'gcse-python' | 'as-cs' | 'al-cs';

export function getQualification(level: Level, subject: Subject): Qualification {
  if (subject === 'ICT') return 'gcse-ict';
  if (subject === 'Python') return 'gcse-python';
  if (level === 'GCSE') return 'gcse-cs';
  if (level === 'AS-Level') return 'as-cs';
  return 'al-cs';
}

export interface SubTopic {
  id: string;
  title: string;
  description: string;
  difficulty: 'Foundation' | 'Higher' | 'Core';
  estimatedMins: number;
}

export interface SyllabusTopic {
  id: string;
  unit: string;
  title: string;
  icon: string;
  color: string;
  iconColor: string;
  borderColor: string;
  subtopics: SubTopic[];
}

export interface BoardSyllabus {
  board: ExamBoard;
  level: Level;
  subject: Subject;
  specCode: string;
  examYear: string;
  paper1: string;
  paper2: string;
  totalTopics: number;
  topics: SyllabusTopic[];
}

export const syllabusData: BoardSyllabus[] = [
  // ─── AQA GCSE Computer Science ───────────────────────────────────────────
  {
    board: 'AQA',
    level: 'GCSE',
    subject: 'Computer Science',
    specCode: '8525',
    examYear: '2025',
    paper1: '14 May 2025',
    paper2: '4 June 2025',
    totalTopics: 8,
    topics: [
      {
        id: 'aqa-gcse-cs-1',
        unit: '3.1',
        title: 'Fundamentals of Algorithms',
        icon: 'ri-flow-chart',
        color: 'bg-violet-50',
        iconColor: 'text-violet-600',
        borderColor: 'border-violet-200',
        subtopics: [
          { id: 'aqa-gcse-cs-1-1', title: 'Representing Algorithms', description: 'Algorithms can be expressed using flowcharts, pseudocode or structured English. Each method has its own conventions and is used to plan logic before coding begins.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-cs-1-2', title: 'Efficiency of Algorithms', description: 'Two algorithms can solve the same problem but differ in speed and memory use. Comparing algorithms helps choose the most suitable one for a given context.', difficulty: 'Higher', estimatedMins: 40 },
          { id: 'aqa-gcse-cs-1-3', title: 'Searching Algorithms', description: 'Linear search checks each item in order; binary search repeatedly halves a sorted list. Binary search is faster but requires the data to be sorted first.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'aqa-gcse-cs-1-4', title: 'Sorting Algorithms', description: 'Bubble sort repeatedly swaps adjacent items; merge sort divides and recombines; insertion sort builds a sorted section one item at a time. Each has different performance characteristics.', difficulty: 'Higher', estimatedMins: 45 },
        ],
      },
      {
        id: 'aqa-gcse-cs-2',
        unit: '3.2',
        title: 'Programming',
        icon: 'ri-code-s-slash-line',
        color: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        borderColor: 'border-emerald-200',
        subtopics: [
          { id: 'aqa-gcse-cs-2-1', title: 'Data Types', description: 'Programs use integer, real, Boolean, character and string types. Casting converts a value from one type to another, for example turning a string into an integer.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-cs-2-2', title: 'Programming Concepts', description: 'Sequence runs instructions in order; selection uses conditions to choose a path; iteration repeats a block. Variables store changing values; constants store fixed ones.', difficulty: 'Foundation', estimatedMins: 40 },
          { id: 'aqa-gcse-cs-2-3', title: 'Subroutines', description: 'Procedures perform a task without returning a value; functions return a result. Parameters pass data in; return values pass data out. Scope determines where a variable is accessible.', difficulty: 'Higher', estimatedMins: 45 },
          { id: 'aqa-gcse-cs-2-4', title: 'Arrays', description: 'A one-dimensional array stores a list of items under one name. A two-dimensional array stores a grid. Items are accessed using an index starting at zero.', difficulty: 'Higher', estimatedMins: 35 },
          { id: 'aqa-gcse-cs-2-5', title: 'File Handling', description: 'Programs can open a file, read its contents line by line, write new data and close it. File handling allows data to persist between program runs.', difficulty: 'Core', estimatedMins: 30 },
          { id: 'aqa-gcse-cs-2-6', title: 'String Handling', description: 'Common string operations include finding the length, extracting a substring, joining strings with concatenation and converting between upper and lower case.', difficulty: 'Foundation', estimatedMins: 25 },
        ],
      },
      {
        id: 'aqa-gcse-cs-3',
        unit: '3.3',
        title: 'Fundamentals of Data Representation',
        icon: 'ri-database-2-line',
        color: 'bg-orange-50',
        iconColor: 'text-orange-500',
        borderColor: 'border-orange-200',
        subtopics: [
          { id: 'aqa-gcse-cs-3-1', title: 'Number Bases', description: 'Binary uses digits 0 and 1; denary uses 0–9; hexadecimal uses 0–9 and A–F. Converting between these systems is a core skill in data representation.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'aqa-gcse-cs-3-2', title: 'Units of Information', description: 'A bit is the smallest unit of data. Eight bits form a byte. Larger units include kilobyte, megabyte, gigabyte and terabyte, each 1024 times the previous.', difficulty: 'Foundation', estimatedMins: 20 },
          { id: 'aqa-gcse-cs-3-3', title: 'Binary Arithmetic', description: 'Binary addition follows carry rules similar to denary. Overflow occurs when the result exceeds the available bits. Two\'s complement represents negative numbers in binary.', difficulty: 'Higher', estimatedMins: 40 },
          { id: 'aqa-gcse-cs-3-4', title: 'Representing Text', description: 'ASCII assigns a unique number to each character using 7 bits. Unicode extends this to cover characters from all world languages using more bits per character.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-cs-3-5', title: 'Representing Images', description: 'An image is made of pixels. Resolution describes the number of pixels; colour depth describes how many bits represent each pixel. File size equals pixels multiplied by colour depth.', difficulty: 'Higher', estimatedMins: 35 },
          { id: 'aqa-gcse-cs-3-6', title: 'Representing Sound', description: 'Sound is sampled at regular intervals. Sample rate is how many samples per second; bit depth is how many bits per sample. Higher values give better quality but larger files.', difficulty: 'Higher', estimatedMins: 30 },
          { id: 'aqa-gcse-cs-3-7', title: 'Data Compression', description: 'Lossless compression reduces file size without losing data; lossy removes some data permanently. Run-length encoding stores repeated values as a count and value pair.', difficulty: 'Higher', estimatedMins: 40 },
        ],
      },
      {
        id: 'aqa-gcse-cs-4',
        unit: '3.4',
        title: 'Computer Systems',
        icon: 'ri-cpu-line',
        color: 'bg-amber-50',
        iconColor: 'text-amber-600',
        borderColor: 'border-amber-200',
        subtopics: [
          { id: 'aqa-gcse-cs-4-1', title: 'Hardware and Software', description: 'Hardware refers to physical components; software refers to programs. System software manages the hardware; application software performs user tasks.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-cs-4-2', title: 'Boolean Logic', description: 'AND outputs true only when both inputs are true; OR outputs true when at least one is; NOT inverts the input. Truth tables show all possible input and output combinations.', difficulty: 'Higher', estimatedMins: 45 },
          { id: 'aqa-gcse-cs-4-3', title: 'Software Classification', description: 'Operating systems, utilities and programming language translators are types of system software. Word processors, browsers and games are examples of application software.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-cs-4-4', title: 'Systems Architecture', description: 'The Von Neumann model stores programs and data in the same memory. The CPU contains the ALU, control unit and registers. The fetch-decode-execute cycle describes how instructions are processed.', difficulty: 'Higher', estimatedMins: 50 },
        ],
      },
      {
        id: 'aqa-gcse-cs-5',
        unit: '3.5',
        title: 'Fundamentals of Computer Networks',
        icon: 'ri-wifi-line',
        color: 'bg-sky-50',
        iconColor: 'text-sky-600',
        borderColor: 'border-sky-200',
        subtopics: [
          { id: 'aqa-gcse-cs-5-1', title: 'Network Types', description: 'A LAN connects devices in a small area such as a school. A WAN connects LANs across large distances. A PAN covers a very short range around a single person.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-cs-5-2', title: 'Network Topologies', description: 'Star topology connects all devices to a central switch. Bus uses a shared cable. Ring connects devices in a loop. Mesh connects every device to every other device.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'aqa-gcse-cs-5-3', title: 'Network Protocols', description: 'TCP/IP is the foundation of internet communication. HTTP transfers web pages; HTTPS adds encryption. FTP transfers files; SMTP sends email; DNS translates domain names to IP addresses.', difficulty: 'Higher', estimatedMins: 40 },
          { id: 'aqa-gcse-cs-5-4', title: 'Network Security', description: 'Firewalls filter incoming and outgoing traffic. Encryption scrambles data so only authorised parties can read it. MAC address filtering restricts which devices can join a network.', difficulty: 'Higher', estimatedMins: 35 },
        ],
      },
      {
        id: 'aqa-gcse-cs-6',
        unit: '3.6',
        title: 'Fundamentals of Cyber Security',
        icon: 'ri-shield-keyhole-line',
        color: 'bg-rose-50',
        iconColor: 'text-rose-500',
        borderColor: 'border-rose-200',
        subtopics: [
          { id: 'aqa-gcse-cs-6-1', title: 'Cyber Security Threats', description: 'Malware is software designed to cause harm. Phishing tricks users into revealing credentials. Brute force tries every possible password. SQL injection inserts malicious code into database queries.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'aqa-gcse-cs-6-2', title: 'Social Engineering', description: 'Social engineering manipulates people rather than systems. Phishing uses fake emails; blagging involves inventing a scenario; shouldering means watching someone enter a password.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-cs-6-3', title: 'Malicious Code', description: 'A virus attaches to files and spreads when they are opened. A worm spreads across networks without user action. A trojan disguises itself as legitimate software. Ransomware encrypts files and demands payment.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-cs-6-4', title: 'Detecting and Preventing Threats', description: 'Anti-malware software detects and removes threats. Firewalls block unauthorised access. Penetration testing finds vulnerabilities before attackers do. User access levels limit what each person can do.', difficulty: 'Higher', estimatedMins: 40 },
        ],
      },
      {
        id: 'aqa-gcse-cs-7',
        unit: '3.7',
        title: 'Ethical, Legal, Cultural and Environmental Impacts',
        icon: 'ri-scales-3-line',
        color: 'bg-teal-50',
        iconColor: 'text-teal-600',
        borderColor: 'border-teal-200',
        subtopics: [
          { id: 'aqa-gcse-cs-7-1', title: 'Ethical Issues', description: 'Technology raises questions about privacy, surveillance and fairness. The digital divide describes unequal access to technology. Artificial intelligence raises questions about bias and accountability.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-cs-7-2', title: 'Legal Issues', description: 'The Computer Misuse Act makes unauthorised access to computer systems illegal. GDPR regulates how personal data is collected and stored. Copyright law protects original creative works.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'aqa-gcse-cs-7-3', title: 'Environmental Impact', description: 'Data centres consume large amounts of electricity. Manufacturing devices uses rare materials and produces waste. E-waste from discarded electronics is a growing environmental concern.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-cs-7-4', title: 'Cultural Impact', description: 'Technology has changed how people communicate, work and access information. Social media influences culture and public opinion. Automation has replaced some jobs while creating others.', difficulty: 'Foundation', estimatedMins: 25 },
        ],
      },
      {
        id: 'aqa-gcse-cs-8',
        unit: '3.8',
        title: 'Aspects of Software Development',
        icon: 'ri-git-branch-line',
        color: 'bg-indigo-50',
        iconColor: 'text-indigo-600',
        borderColor: 'border-indigo-200',
        subtopics: [
          { id: 'aqa-gcse-cs-8-1', title: 'Software Development Lifecycle', description: 'The lifecycle includes analysis, design, implementation, testing and evaluation. Each stage has specific outputs that feed into the next, ensuring the final product meets requirements.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-cs-8-2', title: 'Testing', description: 'Normal data is valid input; boundary data is at the edge of valid ranges; erroneous data is invalid. A test plan documents what will be tested and the expected outcomes.', difficulty: 'Higher', estimatedMins: 40 },
          { id: 'aqa-gcse-cs-8-3', title: 'Languages and Translators', description: 'High-level languages are closer to human language; low-level languages are closer to machine code. A compiler translates the whole program at once; an interpreter translates line by line.', difficulty: 'Higher', estimatedMins: 35 },
        ],
      },
    ],
  },

  // ─── OCR GCSE Computer Science ───────────────────────────────────────────
  {
    board: 'OCR',
    level: 'GCSE',
    subject: 'Computer Science',
    specCode: 'J277',
    examYear: '2025',
    paper1: '15 May 2025',
    paper2: '5 June 2025',
    totalTopics: 6,
    topics: [
      {
        id: 'ocr-gcse-cs-1',
        unit: '1.1',
        title: 'Systems Architecture',
        icon: 'ri-cpu-line',
        color: 'bg-amber-50',
        iconColor: 'text-amber-600',
        borderColor: 'border-amber-200',
        subtopics: [
          { id: 'ocr-gcse-cs-1-1', title: 'The Purpose of the CPU', description: 'The CPU processes instructions stored in memory. It contains the ALU for calculations, the control unit for managing operations and registers for temporary storage.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'ocr-gcse-cs-1-2', title: 'CPU Performance', description: 'Clock speed measures how many cycles per second the CPU performs. More cores allow parallel processing. A larger cache reduces the time spent fetching data from RAM.', difficulty: 'Higher', estimatedMins: 30 },
          { id: 'ocr-gcse-cs-1-3', title: 'Embedded Systems', description: 'An embedded system is a computer built into a device to perform a specific function. Examples include washing machine controllers, car engine management systems and smart meters.', difficulty: 'Foundation', estimatedMins: 20 },
        ],
      },
      {
        id: 'ocr-gcse-cs-2',
        unit: '1.2',
        title: 'Memory and Storage',
        icon: 'ri-hard-drive-2-line',
        color: 'bg-slate-50',
        iconColor: 'text-slate-600',
        borderColor: 'border-slate-200',
        subtopics: [
          { id: 'ocr-gcse-cs-2-1', title: 'Primary Storage', description: 'RAM is volatile memory that holds the operating system and running programs. ROM is non-volatile and stores the boot instructions. Both are directly accessible by the CPU.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'ocr-gcse-cs-2-2', title: 'Secondary Storage', description: 'Hard disk drives use magnetic platters; solid state drives use flash memory. Optical discs store data as pits and lands. Each type differs in speed, capacity and durability.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'ocr-gcse-cs-2-3', title: 'Units of Storage', description: 'Data is measured in bits and bytes. One kilobyte equals 1024 bytes; one megabyte equals 1024 kilobytes. Understanding these units is essential for calculating file and storage sizes.', difficulty: 'Foundation', estimatedMins: 20 },
          { id: 'ocr-gcse-cs-2-4', title: 'Data Storage', description: 'Numbers are stored in binary. Text uses character encoding such as ASCII. Images store pixel colour values. Sound stores sampled amplitude values at regular time intervals.', difficulty: 'Higher', estimatedMins: 45 },
        ],
      },
      {
        id: 'ocr-gcse-cs-3',
        unit: '1.3',
        title: 'Computer Networks, Connections and Protocols',
        icon: 'ri-wifi-line',
        color: 'bg-sky-50',
        iconColor: 'text-sky-600',
        borderColor: 'border-sky-200',
        subtopics: [
          { id: 'ocr-gcse-cs-3-1', title: 'Networks and Topologies', description: 'A LAN covers a small area; a WAN spans large distances. Star topology uses a central switch; mesh connects every device directly to every other device.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'ocr-gcse-cs-3-2', title: 'Wired and Wireless Networks', description: 'Ethernet cables provide fast, reliable connections. Wi-Fi uses radio waves for wireless access. Bluetooth connects devices over short distances with low power consumption.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'ocr-gcse-cs-3-3', title: 'Protocols and Layers', description: 'The TCP/IP model organises network communication into layers. HTTP transfers web content; HTTPS adds encryption. FTP handles file transfers; SMTP and IMAP manage email sending and retrieval.', difficulty: 'Higher', estimatedMins: 40 },
        ],
      },
      {
        id: 'ocr-gcse-cs-4',
        unit: '1.4',
        title: 'Network Security',
        icon: 'ri-shield-keyhole-line',
        color: 'bg-rose-50',
        iconColor: 'text-rose-500',
        borderColor: 'border-rose-200',
        subtopics: [
          { id: 'ocr-gcse-cs-4-1', title: 'Threats to Networks', description: 'Malware includes viruses, worms and ransomware. Phishing emails trick users into revealing passwords. Brute force attacks try many password combinations. SQL injection targets database-driven websites.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'ocr-gcse-cs-4-2', title: 'Identifying and Preventing Threats', description: 'Firewalls monitor and filter network traffic. Anti-malware software scans for and removes threats. Encryption protects data in transit. Access control limits what each user can do on a system.', difficulty: 'Higher', estimatedMins: 35 },
        ],
      },
      {
        id: 'ocr-gcse-cs-5',
        unit: '2.1',
        title: 'Algorithms',
        icon: 'ri-flow-chart',
        color: 'bg-violet-50',
        iconColor: 'text-violet-600',
        borderColor: 'border-violet-200',
        subtopics: [
          { id: 'ocr-gcse-cs-5-1', title: 'Computational Thinking', description: 'Decomposition breaks a problem into smaller parts. Abstraction removes unnecessary detail. Pattern recognition identifies similarities. Algorithmic thinking designs step-by-step solutions.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'ocr-gcse-cs-5-2', title: 'Designing Algorithms', description: 'Pseudocode uses plain language to describe logic without worrying about syntax. Flowcharts use standard shapes to show the flow of control through an algorithm.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'ocr-gcse-cs-5-3', title: 'Searching and Sorting', description: 'Linear search checks each item in sequence. Binary search halves the search space each time on sorted data. Bubble sort compares adjacent pairs; merge sort divides and recombines.', difficulty: 'Higher', estimatedMins: 45 },
        ],
      },
      {
        id: 'ocr-gcse-cs-6',
        unit: '2.2',
        title: 'Programming Fundamentals',
        icon: 'ri-code-s-slash-line',
        color: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        borderColor: 'border-emerald-200',
        subtopics: [
          { id: 'ocr-gcse-cs-6-1', title: 'Programming Concepts', description: 'Variables store values that can change; constants store fixed values. Data types include integer, float, Boolean and string. Operators perform arithmetic, comparison and logical operations.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'ocr-gcse-cs-6-2', title: 'Selection and Iteration', description: 'IF statements execute code based on a condition. WHILE loops repeat while a condition is true. FOR loops repeat a set number of times. Nested structures combine these constructs.', difficulty: 'Foundation', estimatedMins: 40 },
          { id: 'ocr-gcse-cs-6-3', title: 'Arrays and String Handling', description: 'Arrays store multiple values under one name, accessed by index. String functions include finding length, extracting characters, converting case and searching for substrings.', difficulty: 'Higher', estimatedMins: 35 },
          { id: 'ocr-gcse-cs-6-4', title: 'Subroutines', description: 'Functions return a value; procedures do not. Parameters pass data into a subroutine. Local variables exist only inside the subroutine; global variables are accessible throughout the program.', difficulty: 'Higher', estimatedMins: 40 },
        ],
      },
    ],
  },

  // ─── Edexcel GCSE Computer Science ───────────────────────────────────────
  {
    board: 'Edexcel',
    level: 'GCSE',
    subject: 'Computer Science',
    specCode: '1CP2',
    examYear: '2025',
    paper1: '13 May 2025',
    paper2: '3 June 2025',
    totalTopics: 7,
    topics: [
      {
        id: 'edx-gcse-cs-1',
        unit: 'CP1',
        title: 'Computational Thinking',
        icon: 'ri-brain-line',
        color: 'bg-violet-50',
        iconColor: 'text-violet-600',
        borderColor: 'border-violet-200',
        subtopics: [
          { id: 'edx-gcse-cs-1-1', title: 'Decomposition and Abstraction', description: 'Decomposition splits a complex problem into smaller, manageable sub-problems. Abstraction focuses on the essential features of a problem and ignores irrelevant detail.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'edx-gcse-cs-1-2', title: 'Algorithms', description: 'An algorithm is a precise set of instructions to solve a problem. Algorithms can be written in pseudocode or shown as flowcharts before being coded in a programming language.', difficulty: 'Foundation', estimatedMins: 40 },
          { id: 'edx-gcse-cs-1-3', title: 'Searching and Sorting', description: 'Linear search examines each element in turn. Binary search works on sorted data by halving the search space. Bubble sort and merge sort are two common sorting approaches with different efficiencies.', difficulty: 'Higher', estimatedMins: 45 },
        ],
      },
      {
        id: 'edx-gcse-cs-2',
        unit: 'CP2',
        title: 'Data and Data Representation',
        icon: 'ri-database-2-line',
        color: 'bg-orange-50',
        iconColor: 'text-orange-500',
        borderColor: 'border-orange-200',
        subtopics: [
          { id: 'edx-gcse-cs-2-1', title: 'Binary and Hexadecimal', description: 'Binary uses base 2; denary uses base 10; hexadecimal uses base 16. Converting between these systems is a fundamental skill for understanding how computers store and process data.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'edx-gcse-cs-2-2', title: 'Data Types and Structures', description: 'Integers store whole numbers; floats store decimals; Booleans store true or false; strings store text. Arrays group multiple values of the same type under a single name.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'edx-gcse-cs-2-3', title: 'Representing Images and Sound', description: 'Images are made of pixels; each pixel has a colour value stored in binary. Sound is recorded by sampling amplitude at regular intervals. File size depends on resolution, colour depth and sample rate.', difficulty: 'Higher', estimatedMins: 40 },
        ],
      },
      {
        id: 'edx-gcse-cs-3',
        unit: 'CP3',
        title: 'Computers',
        icon: 'ri-cpu-line',
        color: 'bg-amber-50',
        iconColor: 'text-amber-600',
        borderColor: 'border-amber-200',
        subtopics: [
          { id: 'edx-gcse-cs-3-1', title: 'CPU Architecture', description: 'The Von Neumann model stores programs and data in the same memory. The ALU performs calculations; the control unit manages instruction flow; registers hold data being processed.', difficulty: 'Higher', estimatedMins: 50 },
          { id: 'edx-gcse-cs-3-2', title: 'Memory and Storage', description: 'RAM is fast, volatile primary storage. ROM holds permanent boot instructions. Cache sits between the CPU and RAM for speed. Secondary storage such as SSDs and HDDs holds data long-term.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'edx-gcse-cs-3-3', title: 'Input and Output Devices', description: 'Input devices send data to the computer; output devices receive data from it. Choosing the right device depends on the task, for example a touchscreen for a kiosk or a microphone for voice input.', difficulty: 'Foundation', estimatedMins: 25 },
        ],
      },
      {
        id: 'edx-gcse-cs-4',
        unit: 'CP4',
        title: 'Networks',
        icon: 'ri-wifi-line',
        color: 'bg-sky-50',
        iconColor: 'text-sky-600',
        borderColor: 'border-sky-200',
        subtopics: [
          { id: 'edx-gcse-cs-4-1', title: 'Network Types and Topologies', description: 'LANs cover small areas; WANs span large distances. Star topology uses a central switch; bus uses a shared cable; mesh connects every device to every other device.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'edx-gcse-cs-4-2', title: 'Network Hardware', description: 'Routers direct data between networks. Switches connect devices within a network. Network interface cards allow devices to connect. Transmission media include copper cable, fibre optic and wireless.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'edx-gcse-cs-4-3', title: 'Protocols and the Internet', description: 'TCP/IP is the core protocol suite of the internet. DNS translates domain names to IP addresses. HTTP and HTTPS transfer web pages. IP addresses identify devices on a network.', difficulty: 'Higher', estimatedMins: 40 },
        ],
      },
      {
        id: 'edx-gcse-cs-5',
        unit: 'CP5',
        title: 'Issues and Impact',
        icon: 'ri-scales-3-line',
        color: 'bg-teal-50',
        iconColor: 'text-teal-600',
        borderColor: 'border-teal-200',
        subtopics: [
          { id: 'edx-gcse-cs-5-1', title: 'Cyber Security', description: 'Common threats include malware, phishing and denial-of-service attacks. Defences include firewalls, encryption, strong passwords and keeping software up to date.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'edx-gcse-cs-5-2', title: 'Ethical and Legal Issues', description: 'GDPR sets rules for collecting and storing personal data. The Computer Misuse Act criminalises unauthorised access. Copyright law protects software and digital content from unauthorised copying.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'edx-gcse-cs-5-3', title: 'Environmental Impact', description: 'Running data centres requires significant electricity. Manufacturing devices consumes raw materials. Disposing of old electronics creates e-waste. Sustainable computing aims to reduce these impacts.', difficulty: 'Foundation', estimatedMins: 25 },
        ],
      },
      {
        id: 'edx-gcse-cs-6',
        unit: 'CP6',
        title: 'Programming',
        icon: 'ri-code-s-slash-line',
        color: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        borderColor: 'border-emerald-200',
        subtopics: [
          { id: 'edx-gcse-cs-6-1', title: 'Programming Concepts', description: 'Variables store data that can change; constants store fixed values. Selection uses IF statements to choose between paths. Iteration uses loops to repeat blocks of code.', difficulty: 'Foundation', estimatedMins: 40 },
          { id: 'edx-gcse-cs-6-2', title: 'Subroutines and Scope', description: 'Functions return a value; procedures do not. Parameters pass data in; return values pass data out. Local variables are only accessible inside the subroutine where they are declared.', difficulty: 'Higher', estimatedMins: 40 },
          { id: 'edx-gcse-cs-6-3', title: 'File Handling and Exceptions', description: 'Programs can open, read, write and close files to store data between runs. Exception handling catches runtime errors and allows the program to respond gracefully rather than crashing.', difficulty: 'Higher', estimatedMins: 35 },
        ],
      },
      {
        id: 'edx-gcse-cs-7',
        unit: 'CP7',
        title: 'Software Development',
        icon: 'ri-git-branch-line',
        color: 'bg-indigo-50',
        iconColor: 'text-indigo-600',
        borderColor: 'border-indigo-200',
        subtopics: [
          { id: 'edx-gcse-cs-7-1', title: 'Development Lifecycle', description: 'The waterfall model follows fixed sequential stages. Agile development works in short cycles called sprints, allowing requirements to change. Both approaches have strengths depending on the project.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'edx-gcse-cs-7-2', title: 'Testing and Evaluation', description: 'A test plan specifies inputs, expected outputs and actual outputs. Normal, boundary and erroneous data should all be tested. Evaluation assesses whether the solution meets the original requirements.', difficulty: 'Higher', estimatedMins: 35 },
        ],
      },
    ],
  },

  // ─── WJEC GCSE Computer Science ──────────────────────────────────────────
  {
    board: 'WJEC',
    level: 'GCSE',
    subject: 'Computer Science',
    specCode: 'C500QS',
    examYear: '2025',
    paper1: '12 May 2025',
    paper2: '2 June 2025',
    totalTopics: 5,
    topics: [
      {
        id: 'wjec-gcse-cs-1',
        unit: 'Unit 1',
        title: 'Computational Thinking and Programming',
        icon: 'ri-code-s-slash-line',
        color: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        borderColor: 'border-emerald-200',
        subtopics: [
          { id: 'wjec-gcse-cs-1-1', title: 'Computational Thinking', description: 'Decomposition breaks problems into smaller parts. Abstraction removes unnecessary detail. Pattern recognition identifies common structures. Algorithmic thinking designs step-by-step solutions.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'wjec-gcse-cs-1-2', title: 'Programming Constructs', description: 'Sequence runs instructions in order. Selection uses conditions to choose a path. Iteration repeats a block of code. Variables and constants store data; data types define what kind of data is stored.', difficulty: 'Foundation', estimatedMins: 40 },
          { id: 'wjec-gcse-cs-1-3', title: 'Subroutines and Arrays', description: 'Functions return a value; procedures perform a task without returning one. Arrays store multiple values under one name. Two-dimensional arrays store data in rows and columns.', difficulty: 'Higher', estimatedMins: 40 },
          { id: 'wjec-gcse-cs-1-4', title: 'Algorithms', description: 'Linear and binary search find items in a list. Bubble sort and merge sort arrange items in order. Trace tables follow an algorithm step by step to check its logic.', difficulty: 'Higher', estimatedMins: 45 },
        ],
      },
      {
        id: 'wjec-gcse-cs-2',
        unit: 'Unit 2',
        title: 'Computer Systems',
        icon: 'ri-cpu-line',
        color: 'bg-amber-50',
        iconColor: 'text-amber-600',
        borderColor: 'border-amber-200',
        subtopics: [
          { id: 'wjec-gcse-cs-2-1', title: 'Hardware Components', description: 'The CPU processes instructions. RAM holds data in use; ROM holds permanent boot instructions. Secondary storage keeps data long-term. Input devices send data in; output devices present results.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'wjec-gcse-cs-2-2', title: 'Operating Systems', description: 'An operating system manages hardware resources, provides a user interface and runs application software. It handles memory management, process scheduling and file system organisation.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'wjec-gcse-cs-2-3', title: 'Boolean Logic', description: 'AND, OR and NOT are the three basic logic gates. Truth tables list all possible input combinations and their outputs. Logic circuits combine gates to perform more complex operations.', difficulty: 'Higher', estimatedMins: 45 },
        ],
      },
      {
        id: 'wjec-gcse-cs-3',
        unit: 'Unit 3',
        title: 'Data Representation',
        icon: 'ri-database-2-line',
        color: 'bg-orange-50',
        iconColor: 'text-orange-500',
        borderColor: 'border-orange-200',
        subtopics: [
          { id: 'wjec-gcse-cs-3-1', title: 'Number Systems', description: 'Binary uses base 2; denary uses base 10; hexadecimal uses base 16. Converting between these systems is a core skill. Hexadecimal is often used as a shorthand for binary values.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'wjec-gcse-cs-3-2', title: 'Representing Data', description: 'Text is stored using character encoding such as ASCII or Unicode. Images store pixel colour values. Sound stores sampled amplitude values. All data is ultimately stored as binary.', difficulty: 'Higher', estimatedMins: 40 },
          { id: 'wjec-gcse-cs-3-3', title: 'Compression', description: 'Lossless compression reduces file size without losing any data. Lossy compression achieves greater reduction by permanently removing some data. The choice depends on the use case.', difficulty: 'Higher', estimatedMins: 30 },
        ],
      },
      {
        id: 'wjec-gcse-cs-4',
        unit: 'Unit 4',
        title: 'Networks and Cyber Security',
        icon: 'ri-shield-keyhole-line',
        color: 'bg-rose-50',
        iconColor: 'text-rose-500',
        borderColor: 'border-rose-200',
        subtopics: [
          { id: 'wjec-gcse-cs-4-1', title: 'Network Fundamentals', description: 'LANs connect devices in a small area; WANs span large distances. Topologies describe how devices are connected. Protocols define rules for communication between devices.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'wjec-gcse-cs-4-2', title: 'Cyber Security Threats', description: 'Malware includes viruses, worms and ransomware. Phishing uses deceptive messages to steal credentials. Social engineering manipulates people rather than exploiting technical weaknesses.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'wjec-gcse-cs-4-3', title: 'Security Measures', description: 'Firewalls filter network traffic. Encryption scrambles data so only authorised parties can read it. Strong authentication and access control limit who can use a system and what they can do.', difficulty: 'Higher', estimatedMins: 35 },
        ],
      },
      {
        id: 'wjec-gcse-cs-5',
        unit: 'Unit 5',
        title: 'Impacts of Technology',
        icon: 'ri-scales-3-line',
        color: 'bg-teal-50',
        iconColor: 'text-teal-600',
        borderColor: 'border-teal-200',
        subtopics: [
          { id: 'wjec-gcse-cs-5-1', title: 'Ethical and Legal Issues', description: 'The Computer Misuse Act makes unauthorised access illegal. GDPR regulates personal data. Copyright law protects software and digital content. Ethical issues include privacy, surveillance and the digital divide.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'wjec-gcse-cs-5-2', title: 'Environmental and Social Impact', description: 'Data centres consume large amounts of energy. Manufacturing and disposing of devices creates environmental harm. Technology has changed employment, communication and access to information.', difficulty: 'Foundation', estimatedMins: 25 },
        ],
      },
    ],
  },

  // ─── AQA A-Level Computer Science ────────────────────────────────────────
  {
    board: 'AQA',
    level: 'A-Level',
    subject: 'Computer Science',
    specCode: '7517',
    examYear: '2025',
    paper1: '19 May 2025',
    paper2: '9 June 2025',
    totalTopics: 9,
    topics: [
      {
        id: 'aqa-al-cs-1',
        unit: '4.1',
        title: 'Fundamentals of Programming',
        icon: 'ri-code-s-slash-line',
        color: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        borderColor: 'border-emerald-200',
        subtopics: [
          { id: 'aqa-al-cs-1-1', title: 'Programming Paradigms', description: 'Procedural programming uses sequences of instructions. Object-oriented programming models the world as objects with state and behaviour. Functional programming treats computation as the evaluation of mathematical functions.', difficulty: 'Core', estimatedMins: 50 },
          { id: 'aqa-al-cs-1-2', title: 'Object-Oriented Programming', description: 'A class is a blueprint for objects. Inheritance allows a subclass to reuse code from a parent class. Encapsulation hides internal state. Polymorphism allows different classes to be treated as the same type.', difficulty: 'Higher', estimatedMins: 60 },
          { id: 'aqa-al-cs-1-3', title: 'Recursion', description: 'A recursive function calls itself. Every recursive function needs a base case to stop the recursion. Each call is added to the call stack; too many calls without a base case causes a stack overflow.', difficulty: 'Higher', estimatedMins: 50 },
          { id: 'aqa-al-cs-1-4', title: 'File Handling and Exception Handling', description: 'Programs open files, read or write data and close them. Exception handling uses try and except blocks to catch runtime errors and allow the program to continue or fail gracefully.', difficulty: 'Core', estimatedMins: 40 },
        ],
      },
      {
        id: 'aqa-al-cs-2',
        unit: '4.2',
        title: 'Fundamentals of Data Structures',
        icon: 'ri-stack-line',
        color: 'bg-orange-50',
        iconColor: 'text-orange-500',
        borderColor: 'border-orange-200',
        subtopics: [
          { id: 'aqa-al-cs-2-1', title: 'Arrays, Records and Lists', description: 'Static arrays have a fixed size set at compile time. Dynamic structures such as linked lists can grow and shrink at runtime. Each node in a linked list stores data and a pointer to the next node.', difficulty: 'Core', estimatedMins: 45 },
          { id: 'aqa-al-cs-2-2', title: 'Stacks and Queues', description: 'A stack is a last-in first-out structure; push adds an item and pop removes the top item. A queue is first-in first-out; enqueue adds to the back and dequeue removes from the front.', difficulty: 'Higher', estimatedMins: 45 },
          { id: 'aqa-al-cs-2-3', title: 'Trees and Graphs', description: 'A binary tree has at most two children per node. In-order traversal visits left, root, right. Graphs represent relationships between nodes using edges. Adjacency matrices and lists represent graphs in memory.', difficulty: 'Higher', estimatedMins: 60 },
          { id: 'aqa-al-cs-2-4', title: 'Hash Tables and Dictionaries', description: 'A hash function maps a key to an index in an array. Collisions occur when two keys map to the same index; chaining and open addressing are two ways to resolve them.', difficulty: 'Higher', estimatedMins: 45 },
        ],
      },
      {
        id: 'aqa-al-cs-3',
        unit: '4.3',
        title: 'Fundamentals of Algorithms',
        icon: 'ri-flow-chart',
        color: 'bg-violet-50',
        iconColor: 'text-violet-600',
        borderColor: 'border-violet-200',
        subtopics: [
          { id: 'aqa-al-cs-3-1', title: 'Graph Traversal', description: 'Depth-first search explores as far as possible along each branch before backtracking. Breadth-first search explores all neighbours at the current depth before moving deeper.', difficulty: 'Higher', estimatedMins: 50 },
          { id: 'aqa-al-cs-3-2', title: 'Tree Traversal', description: 'Pre-order visits the root before its children. In-order visits the left subtree, then root, then right. Post-order visits children before the root. Each order has different applications.', difficulty: 'Higher', estimatedMins: 45 },
          { id: 'aqa-al-cs-3-3', title: 'Dijkstra\'s Algorithm', description: 'Dijkstra\'s algorithm finds the shortest path from a source node to all other nodes in a weighted graph. It uses a priority queue to always process the nearest unvisited node next.', difficulty: 'Higher', estimatedMins: 55 },
          { id: 'aqa-al-cs-3-4', title: 'Complexity and Big O', description: 'Big O notation describes how an algorithm\'s time or space requirements grow with input size. O(1) is constant; O(n) is linear; O(n²) is quadratic; O(log n) is logarithmic.', difficulty: 'Higher', estimatedMins: 50 },
        ],
      },
      {
        id: 'aqa-al-cs-4',
        unit: '4.4',
        title: 'Theory of Computation',
        icon: 'ri-function-line',
        color: 'bg-indigo-50',
        iconColor: 'text-indigo-600',
        borderColor: 'border-indigo-200',
        subtopics: [
          { id: 'aqa-al-cs-4-1', title: 'Abstraction and Decomposition', description: 'Abstraction models a problem by focusing on relevant details and hiding complexity. Decomposition breaks a problem into smaller sub-problems that can be solved independently.', difficulty: 'Core', estimatedMins: 35 },
          { id: 'aqa-al-cs-4-2', title: 'Finite State Machines', description: 'An FSM has a finite number of states and transitions between them triggered by inputs. State transition diagrams show states as circles and transitions as labelled arrows.', difficulty: 'Higher', estimatedMins: 55 },
          { id: 'aqa-al-cs-4-3', title: 'Regular Languages', description: 'A regular language can be recognised by a finite state machine. Regular expressions describe patterns in strings using operators such as concatenation, alternation and the Kleene star.', difficulty: 'Higher', estimatedMins: 50 },
          { id: 'aqa-al-cs-4-4', title: 'Turing Machines', description: 'A Turing machine reads and writes symbols on an infinite tape according to a set of rules. The halting problem asks whether a program will finish or run forever; it is undecidable.', difficulty: 'Higher', estimatedMins: 60 },
        ],
      },
      {
        id: 'aqa-al-cs-5',
        unit: '4.5',
        title: 'Fundamentals of Data Representation',
        icon: 'ri-database-2-line',
        color: 'bg-orange-50',
        iconColor: 'text-orange-500',
        borderColor: 'border-orange-200',
        subtopics: [
          { id: 'aqa-al-cs-5-1', title: 'Number Systems', description: 'Binary, hexadecimal and denary conversions. Two\'s complement represents negative integers. Floating point uses a mantissa and exponent to represent real numbers with varying precision.', difficulty: 'Core', estimatedMins: 50 },
          { id: 'aqa-al-cs-5-2', title: 'Normalisation', description: 'A normalised floating point number has its mantissa in a standard form to maximise precision. Underflow occurs when a number is too small to represent; overflow when it is too large.', difficulty: 'Higher', estimatedMins: 45 },
          { id: 'aqa-al-cs-5-3', title: 'Coding Systems', description: 'ASCII and Unicode encode characters as numbers. Error checking codes such as parity bits detect transmission errors. Hamming codes can both detect and correct single-bit errors.', difficulty: 'Higher', estimatedMins: 40 },
        ],
      },
      {
        id: 'aqa-al-cs-6',
        unit: '4.6',
        title: 'Fundamentals of Computer Systems',
        icon: 'ri-cpu-line',
        color: 'bg-amber-50',
        iconColor: 'text-amber-600',
        borderColor: 'border-amber-200',
        subtopics: [
          { id: 'aqa-al-cs-6-1', title: 'Hardware and Software', description: 'System software includes the operating system, utilities and language translators. Application software performs user tasks. The OS manages hardware resources and provides services to applications.', difficulty: 'Core', estimatedMins: 35 },
          { id: 'aqa-al-cs-6-2', title: 'Boolean Algebra', description: 'De Morgan\'s laws allow AND and OR expressions to be rewritten using the other operator. Karnaugh maps simplify Boolean expressions by grouping adjacent cells in a grid.', difficulty: 'Higher', estimatedMins: 60 },
          { id: 'aqa-al-cs-6-3', title: 'Classification of Programming Languages', description: 'Machine code is directly executable by the CPU. Assembly language uses mnemonics for machine code instructions. High-level languages are closer to human language and require translation.', difficulty: 'Core', estimatedMins: 40 },
        ],
      },
      {
        id: 'aqa-al-cs-7',
        unit: '4.7',
        title: 'Fundamentals of Computer Organisation and Architecture',
        icon: 'ri-server-line',
        color: 'bg-slate-50',
        iconColor: 'text-slate-600',
        borderColor: 'border-slate-200',
        subtopics: [
          { id: 'aqa-al-cs-7-1', title: 'Internal Hardware Components', description: 'The CPU connects to memory and I/O devices via buses. The data bus carries data; the address bus carries memory locations; the control bus carries control signals.', difficulty: 'Core', estimatedMins: 45 },
          { id: 'aqa-al-cs-7-2', title: 'The Stored Program Concept', description: 'Programs and data are stored in the same memory. The fetch-decode-execute cycle retrieves an instruction, decodes it and carries it out. Registers such as the PC, MAR and MDR support this process.', difficulty: 'Higher', estimatedMins: 50 },
          { id: 'aqa-al-cs-7-3', title: 'Assembly Language', description: 'Assembly language uses mnemonics such as LDA, STA and ADD. Programs are written for a specific processor architecture. Tracing assembly code requires tracking register and memory values step by step.', difficulty: 'Higher', estimatedMins: 55 },
        ],
      },
      {
        id: 'aqa-al-cs-8',
        unit: '4.8',
        title: 'Consequences of Uses of Computing',
        icon: 'ri-scales-3-line',
        color: 'bg-teal-50',
        iconColor: 'text-teal-600',
        borderColor: 'border-teal-200',
        subtopics: [
          { id: 'aqa-al-cs-8-1', title: 'Individual, Cultural and Ethical Issues', description: 'Computing raises questions about privacy, surveillance, AI bias and the digital divide. Professionals have ethical responsibilities to consider the impact of the systems they build.', difficulty: 'Core', estimatedMins: 35 },
          { id: 'aqa-al-cs-8-2', title: 'Legal Issues', description: 'The Computer Misuse Act criminalises unauthorised access and malware creation. GDPR regulates personal data. Intellectual property law protects software. Professional codes of conduct set standards for behaviour.', difficulty: 'Core', estimatedMins: 35 },
        ],
      },
      {
        id: 'aqa-al-cs-9',
        unit: '4.9',
        title: 'Fundamentals of Communication and Networking',
        icon: 'ri-wifi-line',
        color: 'bg-sky-50',
        iconColor: 'text-sky-600',
        borderColor: 'border-sky-200',
        subtopics: [
          { id: 'aqa-al-cs-9-1', title: 'Communication Basics', description: 'Serial transmission sends one bit at a time; parallel sends multiple bits simultaneously. Synchronous transmission uses a shared clock; asynchronous uses start and stop bits.', difficulty: 'Core', estimatedMins: 35 },
          { id: 'aqa-al-cs-9-2', title: 'Network Topologies and Protocols', description: 'The TCP/IP model has four layers: application, transport, internet and network access. The OSI model has seven layers. Each layer adds a header as data passes down the stack.', difficulty: 'Higher', estimatedMins: 50 },
          { id: 'aqa-al-cs-9-3', title: 'Client-Server and Peer-to-Peer', description: 'In a client-server model, clients request services from a central server. In peer-to-peer, all devices share resources equally. Cloud computing delivers services over the internet on demand.', difficulty: 'Higher', estimatedMins: 40 },
        ],
      },
    ],
  },

  // ─── OCR A-Level Computer Science ────────────────────────────────────────
  {
    board: 'OCR',
    level: 'A-Level',
    subject: 'Computer Science',
    specCode: 'H446',
    examYear: '2025',
    paper1: '16 May 2025',
    paper2: '6 June 2025',
    totalTopics: 6,
    topics: [
      {
        id: 'ocr-al-cs-1',
        unit: '1.1',
        title: 'The Characteristics of Contemporary Processors',
        icon: 'ri-cpu-line',
        color: 'bg-amber-50',
        iconColor: 'text-amber-600',
        borderColor: 'border-amber-200',
        subtopics: [
          { id: 'ocr-al-cs-1-1', title: 'CPU Architecture', description: 'Von Neumann architecture stores programs and data in the same memory. Harvard architecture uses separate memories for instructions and data. RISC processors use simple instructions; CISC processors use complex ones.', difficulty: 'Higher', estimatedMins: 55 },
          { id: 'ocr-al-cs-1-2', title: 'Pipelining and Interrupts', description: 'Pipelining overlaps the fetch, decode and execute stages of multiple instructions. An interrupt is a signal that pauses the current process. The interrupt service routine handles the interrupt before returning.', difficulty: 'Higher', estimatedMins: 50 },
          { id: 'ocr-al-cs-1-3', title: 'Memory Hierarchy', description: 'Registers are fastest but smallest. Cache sits between the CPU and RAM. RAM is larger but slower. Virtual memory uses secondary storage as an extension of RAM when physical memory is full.', difficulty: 'Higher', estimatedMins: 45 },
        ],
      },
      {
        id: 'ocr-al-cs-2',
        unit: '1.2',
        title: 'Software and Software Development',
        icon: 'ri-git-branch-line',
        color: 'bg-indigo-50',
        iconColor: 'text-indigo-600',
        borderColor: 'border-indigo-200',
        subtopics: [
          { id: 'ocr-al-cs-2-1', title: 'Types of Software', description: 'System software manages hardware and provides services to applications. Application software performs user tasks. Programming tools include editors, compilers, interpreters and debuggers.', difficulty: 'Core', estimatedMins: 35 },
          { id: 'ocr-al-cs-2-2', title: 'Software Development Methodologies', description: 'Waterfall follows fixed sequential stages. Agile works in short iterative sprints. Extreme programming emphasises frequent releases and pair programming. The spiral model combines iterative development with risk analysis.', difficulty: 'Core', estimatedMins: 40 },
          { id: 'ocr-al-cs-2-3', title: 'Testing and Maintenance', description: 'Black-box testing checks outputs without knowing the internal code. White-box testing examines the code structure. Regression testing ensures new changes have not broken existing functionality.', difficulty: 'Higher', estimatedMins: 45 },
        ],
      },
      {
        id: 'ocr-al-cs-3',
        unit: '1.3',
        title: 'Exchanging Data',
        icon: 'ri-wifi-line',
        color: 'bg-sky-50',
        iconColor: 'text-sky-600',
        borderColor: 'border-sky-200',
        subtopics: [
          { id: 'ocr-al-cs-3-1', title: 'Compression, Encryption and Hashing', description: 'Lossless compression preserves all data; lossy removes some permanently. Symmetric encryption uses one key; asymmetric uses a public and private key pair. Hashing produces a fixed-length digest from any input.', difficulty: 'Higher', estimatedMins: 55 },
          { id: 'ocr-al-cs-3-2', title: 'Databases and SQL', description: 'Relational databases store data in tables linked by keys. Normalisation removes redundancy. SQL SELECT, INSERT, UPDATE and DELETE statements manipulate data. Transactions ensure data integrity.', difficulty: 'Higher', estimatedMins: 60 },
          { id: 'ocr-al-cs-3-3', title: 'Networks', description: 'The TCP/IP and OSI models describe how data travels across a network. Protocols define rules for communication. Network security measures include firewalls, encryption and intrusion detection systems.', difficulty: 'Higher', estimatedMins: 50 },
        ],
      },
      {
        id: 'ocr-al-cs-4',
        unit: '1.4',
        title: 'Data Types, Data Structures and Algorithms',
        icon: 'ri-stack-line',
        color: 'bg-violet-50',
        iconColor: 'text-violet-600',
        borderColor: 'border-violet-200',
        subtopics: [
          { id: 'ocr-al-cs-4-1', title: 'Data Types and Structures', description: 'Primitive types include integer, float, Boolean and character. Composite structures include arrays, linked lists, stacks, queues, trees and graphs. Each has different performance characteristics.', difficulty: 'Core', estimatedMins: 55 },
          { id: 'ocr-al-cs-4-2', title: 'Algorithms', description: 'Sorting algorithms include bubble sort, merge sort and quicksort. Searching algorithms include linear and binary search. Graph algorithms include depth-first search, breadth-first search and Dijkstra\'s algorithm.', difficulty: 'Higher', estimatedMins: 60 },
          { id: 'ocr-al-cs-4-3', title: 'Complexity', description: 'Big O notation describes how an algorithm scales with input size. O(1) is constant; O(log n) is logarithmic; O(n) is linear; O(n²) is quadratic. Space complexity measures memory usage.', difficulty: 'Higher', estimatedMins: 50 },
        ],
      },
      {
        id: 'ocr-al-cs-5',
        unit: '1.5',
        title: 'Legal, Moral, Cultural and Ethical Issues',
        icon: 'ri-scales-3-line',
        color: 'bg-teal-50',
        iconColor: 'text-teal-600',
        borderColor: 'border-teal-200',
        subtopics: [
          { id: 'ocr-al-cs-5-1', title: 'Legal Issues', description: 'The Computer Misuse Act criminalises unauthorised access and malware. GDPR regulates personal data collection and storage. Copyright law protects software and digital content from unauthorised use.', difficulty: 'Core', estimatedMins: 35 },
          { id: 'ocr-al-cs-5-2', title: 'Moral and Ethical Issues', description: 'AI systems can embed bias from training data. Mass surveillance raises privacy concerns. Computing professionals have a responsibility to consider the societal impact of the systems they create.', difficulty: 'Core', estimatedMins: 35 },
        ],
      },
      {
        id: 'ocr-al-cs-6',
        unit: '2.1',
        title: 'Elements of Computational Thinking',
        icon: 'ri-brain-line',
        color: 'bg-rose-50',
        iconColor: 'text-rose-500',
        borderColor: 'border-rose-200',
        subtopics: [
          { id: 'ocr-al-cs-6-1', title: 'Thinking Abstractly', description: 'Abstraction identifies the essential features of a problem and hides unnecessary detail. Information hiding separates the interface of a module from its implementation.', difficulty: 'Core', estimatedMins: 35 },
          { id: 'ocr-al-cs-6-2', title: 'Thinking Ahead and Concurrently', description: 'Preconditions define what must be true before a function runs. Caching stores frequently used data for fast access. Parallel processing runs multiple tasks simultaneously to improve performance.', difficulty: 'Higher', estimatedMins: 45 },
          { id: 'ocr-al-cs-6-3', title: 'Thinking Logically and Procedurally', description: 'Logical thinking identifies conditions and their consequences. Procedural thinking decomposes a problem into a sequence of steps. Both are essential skills for designing correct and efficient algorithms.', difficulty: 'Higher', estimatedMins: 40 },
        ],
      },
    ],
  },

  // ─── AQA AS-Level Computer Science ───────────────────────────────────────
  {
    board: 'AQA',
    level: 'AS-Level',
    subject: 'Computer Science',
    specCode: '7516',
    examYear: '2025',
    paper1: '19 May 2025',
    paper2: '9 June 2025',
    totalTopics: 5,
    topics: [
      {
        id: 'aqa-as-cs-1',
        unit: '3.1',
        title: 'Fundamentals of Programming',
        icon: 'ri-code-s-slash-line',
        color: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        borderColor: 'border-emerald-200',
        subtopics: [
          { id: 'aqa-as-cs-1-1', title: 'Data Types and Variables', description: 'Programs use integer, real, Boolean, character and string types. Variables store values that change; constants store fixed values. Type casting converts between types.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-as-cs-1-2', title: 'Sequence, Selection and Iteration', description: 'Sequence runs instructions in order. Selection uses IF statements to choose a path. Iteration uses FOR and WHILE loops to repeat blocks of code.', difficulty: 'Foundation', estimatedMins: 40 },
          { id: 'aqa-as-cs-1-3', title: 'Subroutines and Scope', description: 'Functions return a value; procedures do not. Parameters pass data in; return values pass data out. Local variables exist only inside the subroutine.', difficulty: 'Higher', estimatedMins: 45 },
          { id: 'aqa-as-cs-1-4', title: 'Recursion', description: 'A recursive function calls itself with a simpler version of the problem. A base case stops the recursion. Each call is placed on the call stack until the base case is reached.', difficulty: 'Higher', estimatedMins: 50 },
        ],
      },
      {
        id: 'aqa-as-cs-2',
        unit: '3.2',
        title: 'Fundamentals of Data Structures',
        icon: 'ri-stack-line',
        color: 'bg-orange-50',
        iconColor: 'text-orange-500',
        borderColor: 'border-orange-200',
        subtopics: [
          { id: 'aqa-as-cs-2-1', title: 'Arrays and Records', description: 'One-dimensional arrays store a list; two-dimensional arrays store a grid. Records group related fields of different types under one name.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'aqa-as-cs-2-2', title: 'Stacks and Queues', description: 'A stack is last-in first-out; push adds and pop removes the top item. A queue is first-in first-out; enqueue adds to the back and dequeue removes from the front.', difficulty: 'Higher', estimatedMins: 45 },
          { id: 'aqa-as-cs-2-3', title: 'Linked Lists', description: 'Each node in a linked list stores data and a pointer to the next node. Nodes can be inserted or deleted without shifting other elements, unlike arrays.', difficulty: 'Higher', estimatedMins: 40 },
        ],
      },
      {
        id: 'aqa-as-cs-3',
        unit: '3.3',
        title: 'Fundamentals of Algorithms',
        icon: 'ri-flow-chart',
        color: 'bg-violet-50',
        iconColor: 'text-violet-600',
        borderColor: 'border-violet-200',
        subtopics: [
          { id: 'aqa-as-cs-3-1', title: 'Searching and Sorting', description: 'Linear search checks each item in turn. Binary search halves a sorted list each time. Bubble sort compares adjacent pairs; merge sort divides and recombines.', difficulty: 'Foundation', estimatedMins: 45 },
          { id: 'aqa-as-cs-3-2', title: 'Algorithm Efficiency', description: 'Big O notation describes how an algorithm scales with input size. O(1) is constant; O(n) is linear; O(n²) is quadratic; O(log n) is logarithmic.', difficulty: 'Higher', estimatedMins: 50 },
        ],
      },
      {
        id: 'aqa-as-cs-4',
        unit: '3.4',
        title: 'Theory of Computation',
        icon: 'ri-function-line',
        color: 'bg-indigo-50',
        iconColor: 'text-indigo-600',
        borderColor: 'border-indigo-200',
        subtopics: [
          { id: 'aqa-as-cs-4-1', title: 'Abstraction and Decomposition', description: 'Abstraction focuses on essential features and hides unnecessary detail. Decomposition breaks a problem into smaller sub-problems that can be solved independently.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-as-cs-4-2', title: 'Finite State Machines', description: 'An FSM has a finite number of states and transitions triggered by inputs. State transition diagrams show states as circles and transitions as labelled arrows.', difficulty: 'Higher', estimatedMins: 55 },
        ],
      },
      {
        id: 'aqa-as-cs-5',
        unit: '3.5',
        title: 'Fundamentals of Computer Systems',
        icon: 'ri-cpu-line',
        color: 'bg-amber-50',
        iconColor: 'text-amber-600',
        borderColor: 'border-amber-200',
        subtopics: [
          { id: 'aqa-as-cs-5-1', title: 'Hardware and Software', description: 'System software manages hardware; application software performs user tasks. The operating system provides services to applications and manages resources.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-as-cs-5-2', title: 'Boolean Logic', description: 'AND, OR and NOT are the basic logic operations. Truth tables show all possible input and output combinations. De Morgan\'s laws allow expressions to be rewritten.', difficulty: 'Higher', estimatedMins: 50 },
          { id: 'aqa-as-cs-5-3', title: 'CPU Architecture', description: 'The Von Neumann model stores programs and data in the same memory. The fetch-decode-execute cycle retrieves, decodes and carries out each instruction.', difficulty: 'Higher', estimatedMins: 45 },
        ],
      },
    ],
  },

  // ─── OCR AS-Level Computer Science ───────────────────────────────────────
  {
    board: 'OCR',
    level: 'AS-Level',
    subject: 'Computer Science',
    specCode: 'H046',
    examYear: '2025',
    paper1: '16 May 2025',
    paper2: '6 June 2025',
    totalTopics: 4,
    topics: [
      {
        id: 'ocr-as-cs-1',
        unit: '1.1',
        title: 'The Characteristics of Contemporary Processors',
        icon: 'ri-cpu-line',
        color: 'bg-amber-50',
        iconColor: 'text-amber-600',
        borderColor: 'border-amber-200',
        subtopics: [
          { id: 'ocr-as-cs-1-1', title: 'CPU Architecture', description: 'The CPU contains the ALU, control unit and registers. The fetch-decode-execute cycle processes instructions. Clock speed, cache size and number of cores affect performance.', difficulty: 'Foundation', estimatedMins: 45 },
          { id: 'ocr-as-cs-1-2', title: 'Memory Hierarchy', description: 'Registers are fastest but smallest. Cache sits between CPU and RAM. RAM is larger but slower. Virtual memory uses secondary storage when RAM is full.', difficulty: 'Higher', estimatedMins: 40 },
        ],
      },
      {
        id: 'ocr-as-cs-2',
        unit: '1.2',
        title: 'Software and Software Development',
        icon: 'ri-git-branch-line',
        color: 'bg-indigo-50',
        iconColor: 'text-indigo-600',
        borderColor: 'border-indigo-200',
        subtopics: [
          { id: 'ocr-as-cs-2-1', title: 'Types of Software', description: 'System software manages hardware and provides services. Application software performs user tasks. Programming tools include editors, compilers and debuggers.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'ocr-as-cs-2-2', title: 'Software Development Methodologies', description: 'Waterfall follows fixed sequential stages. Agile works in short iterative sprints. Each approach suits different project types and team sizes.', difficulty: 'Core', estimatedMins: 35 },
        ],
      },
      {
        id: 'ocr-as-cs-3',
        unit: '2.1',
        title: 'Elements of Computational Thinking',
        icon: 'ri-brain-line',
        color: 'bg-rose-50',
        iconColor: 'text-rose-500',
        borderColor: 'border-rose-200',
        subtopics: [
          { id: 'ocr-as-cs-3-1', title: 'Thinking Abstractly', description: 'Abstraction identifies essential features and hides unnecessary detail. Information hiding separates the interface of a module from its implementation.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'ocr-as-cs-3-2', title: 'Thinking Logically and Procedurally', description: 'Logical thinking identifies conditions and consequences. Procedural thinking decomposes a problem into a sequence of steps for designing correct algorithms.', difficulty: 'Higher', estimatedMins: 40 },
        ],
      },
      {
        id: 'ocr-as-cs-4',
        unit: '2.2',
        title: 'Problem Solving and Programming',
        icon: 'ri-code-s-slash-line',
        color: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        borderColor: 'border-emerald-200',
        subtopics: [
          { id: 'ocr-as-cs-4-1', title: 'Programming Concepts', description: 'Variables, constants, data types, operators and control structures are the building blocks of programs. Selection and iteration control the flow of execution.', difficulty: 'Foundation', estimatedMins: 40 },
          { id: 'ocr-as-cs-4-2', title: 'Algorithms', description: 'Linear and binary search find items in a list. Bubble sort and merge sort arrange items in order. Big O notation describes how algorithms scale with input size.', difficulty: 'Higher', estimatedMins: 50 },
        ],
      },
    ],
  },

  // ─── AQA GCSE ICT ─────────────────────────────────────────────────────────
  {
    board: 'AQA',
    level: 'GCSE',
    subject: 'ICT',
    specCode: 'ICT-AQA',
    examYear: '2025',
    paper1: '14 May 2025',
    paper2: '4 June 2025',
    totalTopics: 6,
    topics: [
      {
        id: 'aqa-gcse-ict-1',
        unit: 'Unit 1',
        title: 'Living in a Digital World',
        icon: 'ri-global-line',
        color: 'bg-sky-50',
        iconColor: 'text-sky-600',
        borderColor: 'border-sky-200',
        subtopics: [
          { id: 'aqa-gcse-ict-1-1', title: 'Digital Devices', description: 'Digital devices include computers, tablets, smartphones and embedded systems. Each has different capabilities, input and output methods and is suited to different tasks.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-ict-1-2', title: 'Connectivity', description: 'Devices connect via wired networks using Ethernet or wirelessly using Wi-Fi and Bluetooth. The internet connects millions of networks worldwide using the TCP/IP protocol suite.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-ict-1-3', title: 'Operating Online', description: 'Online services include email, social media, e-commerce and cloud storage. Users must understand how to stay safe online and protect their personal information.', difficulty: 'Foundation', estimatedMins: 30 },
        ],
      },
      {
        id: 'aqa-gcse-ict-2',
        unit: 'Unit 2',
        title: 'How Organisations Use ICT',
        icon: 'ri-building-line',
        color: 'bg-amber-50',
        iconColor: 'text-amber-600',
        borderColor: 'border-amber-200',
        subtopics: [
          { id: 'aqa-gcse-ict-2-1', title: 'ICT in Business', description: 'Businesses use ICT for communication, data management, marketing and e-commerce. Databases store customer and product information. Spreadsheets support financial planning and analysis.', difficulty: 'Foundation', estimatedMins: 35 },
          { id: 'aqa-gcse-ict-2-2', title: 'ICT in Education', description: 'Schools use virtual learning environments, interactive whiteboards and online resources. ICT supports personalised learning and enables remote education through video conferencing tools.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-ict-2-3', title: 'ICT in Healthcare', description: 'Hospitals use electronic patient records, diagnostic imaging and telemedicine. ICT improves the accuracy and speed of diagnosis and allows patients to access services remotely.', difficulty: 'Foundation', estimatedMins: 25 },
        ],
      },
      {
        id: 'aqa-gcse-ict-3',
        unit: 'Unit 3',
        title: 'Data, Information and Knowledge',
        icon: 'ri-database-2-line',
        color: 'bg-violet-50',
        iconColor: 'text-violet-600',
        borderColor: 'border-violet-200',
        subtopics: [
          { id: 'aqa-gcse-ict-3-1', title: 'Data and Information', description: 'Data is raw facts without context. Information is data that has been processed and given meaning. Knowledge is information that has been understood and can be applied to make decisions.', difficulty: 'Foundation', estimatedMins: 20 },
          { id: 'aqa-gcse-ict-3-2', title: 'Databases', description: 'A database stores data in tables. Records are rows; fields are columns. Queries retrieve specific data. Forms provide a user-friendly interface for entering and viewing records.', difficulty: 'Foundation', estimatedMins: 40 },
          { id: 'aqa-gcse-ict-3-3', title: 'Spreadsheets', description: 'Spreadsheets organise data in rows and columns. Formulas perform calculations. Functions such as SUM, AVERAGE and IF automate common tasks. Charts visualise data for analysis.', difficulty: 'Foundation', estimatedMins: 35 },
        ],
      },
      {
        id: 'aqa-gcse-ict-4',
        unit: 'Unit 4',
        title: 'Safety and Security',
        icon: 'ri-shield-keyhole-line',
        color: 'bg-rose-50',
        iconColor: 'text-rose-500',
        borderColor: 'border-rose-200',
        subtopics: [
          { id: 'aqa-gcse-ict-4-1', title: 'Online Safety', description: 'Online risks include cyberbullying, identity theft and exposure to inappropriate content. Safe practices include using strong passwords, enabling two-factor authentication and being cautious with personal information.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-ict-4-2', title: 'Data Security', description: 'Data can be protected using encryption, access controls and regular backups. Physical security measures such as locked rooms and cable locks also protect hardware and the data it contains.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-ict-4-3', title: 'Legal and Ethical Issues', description: 'GDPR sets rules for collecting and storing personal data. The Computer Misuse Act makes hacking illegal. Copyright law protects digital content. Ethical use of ICT considers the impact on individuals and society.', difficulty: 'Foundation', estimatedMins: 35 },
        ],
      },
      {
        id: 'aqa-gcse-ict-5',
        unit: 'Unit 5',
        title: 'Digital Communication',
        icon: 'ri-chat-3-line',
        color: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        borderColor: 'border-emerald-200',
        subtopics: [
          { id: 'aqa-gcse-ict-5-1', title: 'Email and Messaging', description: 'Email sends messages between addresses using SMTP and IMAP. Instant messaging allows real-time text communication. Both can include attachments and are used widely in business and personal contexts.', difficulty: 'Foundation', estimatedMins: 20 },
          { id: 'aqa-gcse-ict-5-2', title: 'Social Media', description: 'Social media platforms allow users to share content, communicate and build communities. They raise issues around privacy, misinformation and the impact of online behaviour on mental health.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-ict-5-3', title: 'Video Conferencing', description: 'Video conferencing tools allow face-to-face communication over the internet. They are used in business meetings, remote education and healthcare consultations, reducing the need for travel.', difficulty: 'Foundation', estimatedMins: 20 },
        ],
      },
      {
        id: 'aqa-gcse-ict-6',
        unit: 'Unit 6',
        title: 'Impact of Technology on Society',
        icon: 'ri-scales-3-line',
        color: 'bg-teal-50',
        iconColor: 'text-teal-600',
        borderColor: 'border-teal-200',
        subtopics: [
          { id: 'aqa-gcse-ict-6-1', title: 'Employment and Automation', description: 'Automation has replaced many routine jobs while creating new roles in technology. Workers need to adapt by developing digital skills. The gig economy has grown alongside digital platforms.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-ict-6-2', title: 'The Digital Divide', description: 'The digital divide describes unequal access to technology between different groups. Factors include income, geography, age and disability. Bridging the divide is important for equal participation in society.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-ict-6-3', title: 'Environmental Impact', description: 'ICT equipment consumes energy and contains hazardous materials. E-waste is a growing problem. Sustainable practices include energy-efficient hardware, recycling programmes and reducing unnecessary device replacement.', difficulty: 'Foundation', estimatedMins: 25 },
        ],
      },
    ],
  },

  // ─── AQA GCSE Python ─────────────────────────────────────────────────────
  {
    board: 'AQA',
    level: 'GCSE',
    subject: 'Python',
    specCode: 'PY-AQA',
    examYear: '2025',
    paper1: '14 May 2025',
    paper2: '4 June 2025',
    totalTopics: 7,
    topics: [
      {
        id: 'aqa-gcse-py-1',
        unit: 'PY1',
        title: 'Python Basics',
        icon: 'ri-terminal-box-line',
        color: 'bg-emerald-50',
        iconColor: 'text-emerald-600',
        borderColor: 'border-emerald-200',
        subtopics: [
          { id: 'aqa-gcse-py-1-1', title: 'Variables and Data Types', description: 'Variables store values that can change during a program. Python supports integers, floats, strings and Booleans. The type() function returns the data type of a value.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-py-1-2', title: 'Input and Output', description: 'The input() function reads a string from the user. The print() function displays output. Type casting with int() and float() converts strings to numbers for arithmetic.', difficulty: 'Foundation', estimatedMins: 20 },
          { id: 'aqa-gcse-py-1-3', title: 'Arithmetic Operators', description: 'Python supports addition (+), subtraction (-), multiplication (*), division (/), integer division (//), modulus (%) and exponentiation (**). Operator precedence follows standard mathematical rules.', difficulty: 'Foundation', estimatedMins: 25 },
        ],
      },
      {
        id: 'aqa-gcse-py-2',
        unit: 'PY2',
        title: 'Selection',
        icon: 'ri-git-branch-line',
        color: 'bg-violet-50',
        iconColor: 'text-violet-600',
        borderColor: 'border-violet-200',
        subtopics: [
          { id: 'aqa-gcse-py-2-1', title: 'If Statements', description: 'An if statement runs a block of code only when a condition is true. The condition uses comparison operators such as ==, !=, >, < and >=. Indentation defines the block.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-py-2-2', title: 'If-Elif-Else', description: 'elif adds additional conditions to check when the first if is false. else runs when no condition is true. Multiple conditions can be combined using and, or and not.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-py-2-3', title: 'Nested Selection', description: 'An if statement inside another if statement is called nested selection. It allows more complex decision-making but can reduce readability if overused.', difficulty: 'Higher', estimatedMins: 30 },
        ],
      },
      {
        id: 'aqa-gcse-py-3',
        unit: 'PY3',
        title: 'Iteration',
        icon: 'ri-loop-left-line',
        color: 'bg-orange-50',
        iconColor: 'text-orange-500',
        borderColor: 'border-orange-200',
        subtopics: [
          { id: 'aqa-gcse-py-3-1', title: 'For Loops', description: 'A for loop repeats a block of code a set number of times. range(n) generates numbers from 0 to n-1. range(start, stop, step) gives more control over the sequence.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-py-3-2', title: 'While Loops', description: 'A while loop repeats as long as a condition is true. It is used when the number of repetitions is not known in advance. A break statement exits the loop early.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-py-3-3', title: 'Nested Loops', description: 'A loop inside another loop is called a nested loop. The inner loop completes all its iterations for each iteration of the outer loop. Nested loops are useful for working with two-dimensional data.', difficulty: 'Higher', estimatedMins: 35 },
        ],
      },
      {
        id: 'aqa-gcse-py-4',
        unit: 'PY4',
        title: 'Functions',
        icon: 'ri-function-line',
        color: 'bg-indigo-50',
        iconColor: 'text-indigo-600',
        borderColor: 'border-indigo-200',
        subtopics: [
          { id: 'aqa-gcse-py-4-1', title: 'Defining and Calling Functions', description: 'A function is defined with the def keyword followed by a name and parentheses. It is called by writing its name followed by parentheses. Functions help avoid repeating code.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-py-4-2', title: 'Parameters and Return Values', description: 'Parameters are variables listed in the function definition that receive values when the function is called. The return statement sends a value back to the caller.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-py-4-3', title: 'Scope', description: 'A local variable is created inside a function and cannot be accessed outside it. A global variable is created outside all functions and can be accessed anywhere. Using global variables inside functions requires the global keyword.', difficulty: 'Higher', estimatedMins: 30 },
        ],
      },
      {
        id: 'aqa-gcse-py-5',
        unit: 'PY5',
        title: 'Lists and Strings',
        icon: 'ri-list-check',
        color: 'bg-sky-50',
        iconColor: 'text-sky-600',
        borderColor: 'border-sky-200',
        subtopics: [
          { id: 'aqa-gcse-py-5-1', title: 'Lists', description: 'A list stores multiple values in a single variable. Items are accessed by index starting at zero. Lists are mutable, meaning items can be added, removed or changed after creation.', difficulty: 'Foundation', estimatedMins: 30 },
          { id: 'aqa-gcse-py-5-2', title: 'List Methods', description: 'append() adds an item to the end; insert() adds at a specific position; remove() deletes the first matching item; pop() removes by index. len() returns the number of items.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-py-5-3', title: 'String Operations', description: 'Strings support indexing and slicing. len() returns the length. upper() and lower() change case. split() divides a string into a list. in checks whether a substring is present.', difficulty: 'Foundation', estimatedMins: 25 },
        ],
      },
      {
        id: 'aqa-gcse-py-6',
        unit: 'PY6',
        title: 'File Handling',
        icon: 'ri-file-text-line',
        color: 'bg-amber-50',
        iconColor: 'text-amber-600',
        borderColor: 'border-amber-200',
        subtopics: [
          { id: 'aqa-gcse-py-6-1', title: 'Reading Files', description: 'open() opens a file; read() reads the entire contents; readline() reads one line; readlines() returns a list of lines. The with statement ensures the file is closed automatically.', difficulty: 'Core', estimatedMins: 30 },
          { id: 'aqa-gcse-py-6-2', title: 'Writing Files', description: 'Opening a file with mode "w" overwrites it; mode "a" appends to it. write() writes a string to the file. Newline characters must be added manually when writing multiple lines.', difficulty: 'Core', estimatedMins: 25 },
        ],
      },
      {
        id: 'aqa-gcse-py-7',
        unit: 'PY7',
        title: 'Algorithms in Python',
        icon: 'ri-flow-chart',
        color: 'bg-rose-50',
        iconColor: 'text-rose-500',
        borderColor: 'border-rose-200',
        subtopics: [
          { id: 'aqa-gcse-py-7-1', title: 'Linear Search in Python', description: 'A linear search iterates through a list checking each item against the target. It returns the index when found or -1 if not found. It works on unsorted data but is slow for large lists.', difficulty: 'Foundation', estimatedMins: 25 },
          { id: 'aqa-gcse-py-7-2', title: 'Binary Search in Python', description: 'Binary search requires a sorted list. It compares the target to the middle item and halves the search space each time. It is much faster than linear search for large sorted datasets.', difficulty: 'Higher', estimatedMins: 35 },
          { id: 'aqa-gcse-py-7-3', title: 'Bubble Sort in Python', description: 'Bubble sort compares adjacent items and swaps them if they are in the wrong order. It repeats until no swaps are needed. It is simple to implement but inefficient for large lists.', difficulty: 'Higher', estimatedMins: 35 },
        ],
      },
    ],
  },
];

export const boardColors: Record<ExamBoard, { bg: string; text: string; border: string; badge: string }> = {
  AQA: { bg: 'bg-rose-500', text: 'text-white', border: 'border-rose-500', badge: 'bg-rose-100 text-rose-700' },
  OCR: { bg: 'bg-teal-500', text: 'text-white', border: 'border-teal-500', badge: 'bg-teal-100 text-teal-700' },
  Edexcel: { bg: 'bg-amber-500', text: 'text-white', border: 'border-amber-500', badge: 'bg-amber-100 text-amber-700' },
  WJEC: { bg: 'bg-indigo-500', text: 'text-white', border: 'border-indigo-500', badge: 'bg-indigo-100 text-indigo-700' },
};

export const subjectColors: Record<Subject, { bg: string; text: string; badge: string }> = {
  'Computer Science': { bg: 'bg-slate-800', text: 'text-white', badge: 'bg-slate-100 text-slate-700' },
  'ICT': { bg: 'bg-sky-600', text: 'text-white', badge: 'bg-sky-100 text-sky-700' },
  'Python': { bg: 'bg-emerald-600', text: 'text-white', badge: 'bg-emerald-100 text-emerald-700' },
};

export const difficultyColors: Record<string, string> = {
  Foundation: 'bg-emerald-100 text-emerald-700',
  Higher: 'bg-orange-100 text-orange-700',
  Core: 'bg-sky-100 text-sky-700',
};
